import React, { useMemo, useEffect, useRef, useState } from 'react';
import { calculateEmployerESI, calculateEmployerPF, calculateESI, calculatePF, employeePfLabel, formatWageCapSourceLabel, isLimitEnabled, resolveSalaryWageCaps } from '@/lib/payrollCalc';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp } from 'lucide-react';
import { SalaryIncrementDialog } from '@/components/SalaryIncrementDialog';

const fmt = (v) => Number(v || 0).toLocaleString('en-IN');
const ann = (v) => Number(v || 0) * 12;

function SalaryRow({ label, monthly, annual, editable, field, onChange, highlight, bold, testId, required = false, hideAnnual = false }) {
  return (
    <tr className={`${highlight ? 'bg-muted/60' : ''} ${bold ? 'font-semibold' : ''}`}>
      <td className="py-2 px-3 text-sm border-b border-border">{label}</td>
      <td className="py-2 px-3 border-b border-border text-right">
        {editable ? (
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">&#8377;</span>
            <Input
              data-testid={testId || `input-salary-${field}`}
              type="number"
              min="0"
              required={required}
              value={monthly}
              onChange={e => onChange(field, e.target.value)}
              className="pl-6 font-mono text-sm h-8 w-32 text-right ml-auto"
            />
          </div>
        ) : (
          <span className={`font-mono text-sm ${bold ? 'text-[#0047AB]' : ''}`}>&#8377;{fmt(monthly)}</span>
        )}
      </td>
      <td className="py-2 px-3 border-b border-border text-right">
        {hideAnnual ? (
          <span className="font-mono text-sm text-muted-foreground">—</span>
        ) : (
          <span className={`font-mono text-sm ${bold ? 'text-[#0047AB]' : ''}`}>&#8377;{fmt(annual)}</span>
        )}
      </td>
    </tr>
  );
}

function CapLimitRow({ label, enabled, monthly, field, onChange, testId }) {
  return (
    <tr>
      <td className="py-2 px-3 text-sm border-b border-border">{label}</td>
      <td className="py-2 px-3 border-b border-border text-right">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">&#8377;</span>
          <Input
            data-testid={testId || `input-salary-${field}`}
            type="number"
            min="0"
            disabled={!enabled}
            value={enabled ? monthly : 0}
            onChange={(e) => onChange(field, e.target.value)}
            className="pl-6 font-mono text-sm h-8 w-32 text-right ml-auto disabled:opacity-50"
          />
        </div>
      </td>
      <td className="py-2 px-3 border-b border-border text-right">
        <span className="font-mono text-sm text-muted-foreground">—</span>
      </td>
    </tr>
  );
}

function SectionHeader({ label, code, action }) {
  return (
    <tr className="bg-[#0047AB]/5">
      <td colSpan={3} className="py-2 px-3 text-xs font-bold uppercase tracking-widest text-[#0047AB] border-b border-[#0047AB]/20">
        <div className="flex items-center gap-2">
          <span>
            {label} {code && <span className="text-muted-foreground">({code})</span>}
          </span>
          {action}
        </div>
      </td>
    </tr>
  );
}

const buildExtraReimbursement = (frequency, amount, inHand, ctc) => {
  const freq = frequency === 'Yearly' ? 'Yearly' : 'Monthly';
  const amt = Number(amount) || 0;
  const monthlyExtra = freq === 'Yearly' ? Math.round(amt / 12) : amt;
  const annualExtra = freq === 'Yearly' ? amt : amt * 12;
  return {
    extraReimbursementFrequency: freq,
    extraReimbursementAmount: amt,
    inHandSalaryAfterExtraReimbursement: inHand + monthlyExtra,
    inHandSalaryAfterExtraReimbursementPerAnnum: inHand * 12 + annualExtra,
    totalCtcAfterExtraReimbursement: ctc + monthlyExtra,
    totalCtcAfterExtraReimbursementPerAnnum: ctc * 12 + annualExtra,
  };
};

const capSourceLabel = (source, projectLabel) => formatWageCapSourceLabel(source, projectLabel);

export function SalaryTab({ form, projects = [], employeeId, handleSalaryChange, handleExtraReimbursementChange, onSalaryIncremented }) {
  const sal = form.salary || {};
  const basic = Number(sal.basic) || 0;
  const hra = Number(sal.hra) || 0;
  const specialAllowance = Number(sal.specialAllowance) || 0;
  const conveyance = Number(sal.conveyance) || 0;
  const telephoneExpense = Number(sal.telephoneExpense) || 0;
  const mediclaim = Number(sal.mediclaim) || 0;
  const tds = Number(sal.tds) || 0;
  const epf = Number(sal.epf) || 0;
  const esic = Number(sal.esic) || 0;
  const bonus = Number(sal.bonus) || 0;
  const employerPF = Number(sal.employerPF) || 0;
  const employerESIC = Number(sal.employerESIC) || 0;
  const gratuity = Number(sal.gratuity) || 0;
  const applyPfCeilingToEmployee = Boolean(sal.applyPfCeilingToEmployee);
  const employerPfLimitEnabled = isLimitEnabled(sal.employerPfLimit);
  const esicThresholdEnabled = isLimitEnabled(sal.esicThreshold);
  const capsEnabled = employerPfLimitEnabled || esicThresholdEnabled;
  const employerPfLimitInput = employerPfLimitEnabled ? Number(sal.employerPfLimit) : 0;
  const esicThresholdInput = esicThresholdEnabled ? Number(sal.esicThreshold) : 0;
  const gross = basic + hra + specialAllowance;
  const wageCaps = resolveSalaryWageCaps(sal, {
    assignedProjectIds: form?.projects,
    projects,
  });

  const extra = sal.extraReimbursement || {};
  const extraFrequency = extra.extraReimbursementFrequency === 'Yearly' ? 'Yearly' : 'Monthly';
  const extraAmount = Number(extra.extraReimbursementAmount) || 0;

  const handleSalaryFieldChange = (field, value) => {
    handleSalaryChange(field, value);

    const parsedValue = Number(value) || 0;
    const nextBasic = field === 'basic' ? parsedValue : basic;
    const nextHra = field === 'hra'
      ? parsedValue
      : (field === 'basic' || field === 'specialAllowance' ? Math.round(nextBasic * 0.4) : hra);
    const nextSpecialAllowance = field === 'specialAllowance' ? parsedValue : specialAllowance;
    const nextGross = nextBasic + nextHra + nextSpecialAllowance;
    const nextLimitValue = (currentField, currentValue) => {
      if (currentField !== field) return sal[currentField];
      if (currentValue == null) return null;
      const n = Number(currentValue);
      return Number.isNaN(n) || n < 0 ? 0 : n;
    };
    const nextSalaryForCaps = {
      ...sal,
      employerPfLimit: nextLimitValue('employerPfLimit', field === 'employerPfLimit' ? value : sal.employerPfLimit),
      esicThreshold: nextLimitValue('esicThreshold', field === 'esicThreshold' ? value : sal.esicThreshold),
    };
    const nextCaps = resolveSalaryWageCaps(nextSalaryForCaps, {
      assignedProjectIds: form?.projects,
      projects,
    });
    const nextPfLimit = nextCaps.pfLimit;
    const nextEsicThreshold = nextCaps.esicThreshold;

    if (field === 'basic' || field === 'hra' || field === 'specialAllowance') {
      if (field !== 'hra') handleSalaryChange('hra', Math.round(nextBasic * 0.4));
      if (field !== 'epf') handleSalaryChange('epf', calculatePF(nextBasic, applyPfCeilingToEmployee));
      if (field !== 'esic') handleSalaryChange('esic', calculateESI(nextGross, nextEsicThreshold));
      if (field !== 'bonus') handleSalaryChange('bonus', Math.round(nextBasic * 0.0833));
      if (field !== 'employerPF') handleSalaryChange('employerPF', calculateEmployerPF(nextBasic, nextPfLimit));
      if (field !== 'employerESIC') handleSalaryChange('employerESIC', calculateEmployerESI(nextGross, nextEsicThreshold));
      if (field !== 'gratuity') handleSalaryChange('gratuity', Math.round(nextBasic * 0.0481));
    }

    if (field === 'employerPfLimit') {
      handleSalaryChange('employerPF', calculateEmployerPF(nextBasic, nextPfLimit));
    }

    if (field === 'esicThreshold') {
      handleSalaryChange('esic', calculateESI(nextGross, nextEsicThreshold));
      handleSalaryChange('employerESIC', calculateEmployerESI(nextGross, nextEsicThreshold));
    }
  };

  const handleEmployeePfCeilingToggle = (on) => {
    handleSalaryChange('applyPfCeilingToEmployee', on);
    handleSalaryChange('epf', calculatePF(basic, on));
  };

  const handleCapsToggle = (on) => {
    const nextPf = on ? (employerPfLimitEnabled ? employerPfLimitInput : 0) : null;
    const nextEsic = on ? (esicThresholdEnabled ? esicThresholdInput : 0) : null;
    handleSalaryChange('employerPfLimit', nextPf);
    handleSalaryChange('esicThreshold', nextEsic);
    const nextCaps = resolveSalaryWageCaps(
      { ...sal, employerPfLimit: nextPf, esicThreshold: nextEsic },
      { assignedProjectIds: form?.projects, projects }
    );
    handleSalaryChange('employerPF', calculateEmployerPF(basic, nextCaps.pfLimit));
    handleSalaryChange('esic', calculateESI(gross, nextCaps.esicThreshold));
    handleSalaryChange('employerESIC', calculateEmployerESI(gross, nextCaps.esicThreshold));
  };

  const [incrementOpen, setIncrementOpen] = useState(false);

  const calc = useMemo(() => {
    const totalReimb = conveyance;
    const totalDed = epf + esic + tds;
    const inHand = gross + totalReimb - totalDed;
    const employerTotal = employerPF + employerESIC + gratuity + mediclaim + telephoneExpense;
    const ctc = gross + totalReimb + bonus + employerTotal;
    return { gross, totalReimb, epf, esic, totalDed, inHand, bonus, employerPF, employerESIC, gratuity, employerTotal, ctc };
  }, [basic, hra, specialAllowance, conveyance, telephoneExpense, mediclaim, tds, epf, esic, bonus, employerPF, employerESIC, gratuity]);

  const extraCalc = useMemo(
    () => buildExtraReimbursement(extraFrequency, extraAmount, calc.inHand, calc.ctc),
    [extraFrequency, extraAmount, calc.inHand, calc.ctc]
  );

  const lastExtraJsonRef = useRef('');
  useEffect(() => {
    if (!handleExtraReimbursementChange) return;
    const nextJson = JSON.stringify(extraCalc);
    if (nextJson === lastExtraJsonRef.current) return;
    lastExtraJsonRef.current = nextJson;
    handleExtraReimbursementChange(extraCalc);
  }, [extraCalc, handleExtraReimbursementChange]);

  useEffect(() => {
    const nextEmployerPF = calculateEmployerPF(basic, wageCaps.pfLimit);
    const nextEsic = calculateESI(gross, wageCaps.esicThreshold);
    const nextEmployerESIC = calculateEmployerESI(gross, wageCaps.esicThreshold);
    if (employerPF !== nextEmployerPF) handleSalaryChange('employerPF', nextEmployerPF);
    if (esic !== nextEsic) handleSalaryChange('esic', nextEsic);
    if (employerESIC !== nextEmployerESIC) handleSalaryChange('employerESIC', nextEmployerESIC);
  }, [basic, gross, wageCaps.pfLimit, wageCaps.esicThreshold]);

  const monthlyExtraDisplay =
    extraFrequency === 'Yearly' ? Math.round(extraAmount / 12) : extraAmount;
  const annualExtraDisplay =
    extraFrequency === 'Yearly' ? extraAmount : extraAmount * 12;

  return (
    <Card className="border border-border shadow-sm" data-testid="salary-ctc-card">
      <CardHeader className="pb-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between space-y-0">
        <div className="min-w-0">
          <CardTitle className="text-base font-[Manrope]">Salary CTC Structure</CardTitle>
          <p className="text-xs text-muted-foreground">Enter monthly values. Turn statutory caps off to use project or system default. When on, entered amounts are used (including 0).</p>
          {wageCaps.projectLabel && (
            <Badge
              variant="outline"
              className="mt-2 w-fit max-w-full whitespace-normal text-left font-normal text-[#0047AB] border-[#0047AB]/30 bg-[#0047AB]/5"
              data-testid="project-caps-label"
            >
              PF/ESIC calculated using project: {wageCaps.projectLabel}
            </Badge>
          )}
        </div>
        {employeeId ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setIncrementOpen(true)}
            className="shrink-0 self-start bg-[#0047AB] hover:bg-[#003d96] text-white"
            data-testid="increment-salary-btn"
          >
            <TrendingUp className="w-4 h-4 mr-2" /> Increment
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="salary-table">
            <thead>
              <tr className="bg-muted/80">
                <th className="py-2 px-3 text-left text-xs uppercase tracking-widest font-bold text-muted-foreground border-b-2 border-border w-[50%]">Salary Component</th>
                <th className="py-2 px-3 text-right text-xs uppercase tracking-widest font-bold text-muted-foreground border-b-2 border-border w-[25%]">Per Month</th>
                <th className="py-2 px-3 text-right text-xs uppercase tracking-widest font-bold text-muted-foreground border-b-2 border-border w-[25%]">Per Annum</th>
              </tr>
            </thead>
            <tbody>
              {/* GROSS SALARY Section */}
              <SectionHeader label="Earnings" />
              <SalaryRow label="Basic" monthly={basic} annual={ann(basic)} editable field="basic" onChange={handleSalaryFieldChange} />
              <SalaryRow label="House Rent Allowance" monthly={hra} annual={ann(hra)} editable field="hra" onChange={handleSalaryFieldChange} />
              <SalaryRow label="Special Allowance" monthly={specialAllowance} annual={ann(specialAllowance)} editable field="specialAllowance" onChange={handleSalaryFieldChange} />
              <SalaryRow label="Gross Salary (A)" monthly={calc.gross} annual={ann(calc.gross)} highlight bold />
              <SalaryRow
                label="Salary as on March"
                monthly={Number(sal.salaryAsOnMarch) || 0}
                annual={0}
                hideAnnual
                editable
                field="salaryAsOnMarch"
                onChange={handleSalaryChange}
                testId="input-salary-as-on-march"
              />

              <SectionHeader
                label="Statutory Caps"
                action={(
                  <>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                      {capsEnabled ? 'On' : 'Off'}
                    </span>
                    <Switch
                      checked={capsEnabled}
                      onCheckedChange={handleCapsToggle}
                      aria-label="Enable statutory caps"
                      data-testid="toggle-salary-caps"
                    />
                  </>
                )}
              />
              <CapLimitRow
                label="Employer PF Limit (employee override)"
                enabled={capsEnabled}
                monthly={employerPfLimitInput}
                field="employerPfLimit"
                onChange={handleSalaryFieldChange}
              />
              <CapLimitRow
                label="ESIC Threshold (employee override)"
                enabled={capsEnabled}
                monthly={esicThresholdInput}
                field="esicThreshold"
                onChange={handleSalaryFieldChange}
              />
              <SalaryRow
                label={`Applied Employer PF Limit (${capSourceLabel(wageCaps.pfSource, wageCaps.projectLabel)})`}
                monthly={wageCaps.pfLimit}
                annual={wageCaps.pfLimit}
                hideAnnual
              />
              <SalaryRow
                label={`Applied ESIC Threshold (${capSourceLabel(wageCaps.esicSource, wageCaps.projectLabel)})`}
                monthly={wageCaps.esicThreshold}
                annual={wageCaps.esicThreshold}
                hideAnnual
              />

              {/* REIMBURSEMENTS Section */}
              <SectionHeader label="Reimbursements" code="B" />
              <SalaryRow label="Conveyance Reimbursements (Driver, Petrol)" monthly={conveyance} annual={ann(conveyance)} editable field="conveyance" onChange={handleSalaryChange} />
              <SalaryRow label="Total Reimbursements (B)" monthly={calc.totalReimb} annual={ann(calc.totalReimb)} highlight bold />

              <tr data-testid="extra-reimbursement-row">
                <td className="py-2 px-3 text-sm border-b border-border">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <span>Extra Reimbursement</span>
                    <Select
                      value={extraFrequency}
                      onValueChange={(v) =>
                        handleExtraReimbursementChange?.(
                          buildExtraReimbursement(v, extraAmount, calc.inHand, calc.ctc)
                        )
                      }
                    >
                      <SelectTrigger
                        className="h-8 w-[120px]"
                        data-testid="input-extra-reimbursement-frequency"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="Yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </td>
                <td className="py-2 px-3 border-b border-border text-right">
                  <div className="relative inline-block">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">&#8377;</span>
                    <Input
                      data-testid="input-extra-reimbursement-amount"
                      type="number"
                      min="0"
                      value={extraAmount}
                      onChange={(e) =>
                        handleExtraReimbursementChange?.(
                          buildExtraReimbursement(
                            extraFrequency,
                            e.target.value,
                            calc.inHand,
                            calc.ctc
                          )
                        )
                      }
                      className="pl-6 font-mono text-sm h-8 w-32 text-right"
                    />
                  </div>
                </td>
                <td className="py-2 px-3 border-b border-border text-right">
                  <span className="font-mono text-sm">&#8377;{fmt(annualExtraDisplay)}</span>
                  {extraFrequency === 'Yearly' && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">&#8377;{fmt(monthlyExtraDisplay)}/mo</p>
                  )}
                </td>
              </tr>

              {/* DEDUCTIONS Section */}
              <SectionHeader
                label="Employee Deductions"
                code="C"
                action={(
                  <>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                      PF ceiling {applyPfCeilingToEmployee ? 'On' : 'Off'}
                    </span>
                    <Switch
                      checked={applyPfCeilingToEmployee}
                      onCheckedChange={handleEmployeePfCeilingToggle}
                      aria-label="Apply PF ceiling of 15000 to employee PF"
                      data-testid="toggle-employee-pf-ceiling"
                    />
                  </>
                )}
              />
              <SalaryRow label={employeePfLabel(applyPfCeilingToEmployee)} monthly={epf} annual={ann(epf)} editable field="epf" onChange={handleSalaryFieldChange} />
              <SalaryRow label="ESIC (0.75% of Gross)" monthly={esic} annual={ann(esic)} editable field="esic" onChange={handleSalaryFieldChange} />
              <SalaryRow label="TDS" monthly={tds} annual={ann(tds)} editable field="tds" onChange={handleSalaryFieldChange} />
              <SalaryRow label="Total Deductions (C)" monthly={calc.totalDed} annual={ann(calc.totalDed)} highlight bold />

              {/* IN HAND SALARY */}
              <tr className="bg-[#10b981]/10">
                <td className="py-3 px-3 text-sm font-bold border-b border-[#10b981]/30">In Hand Salary (A + B - C)</td>
                <td className="py-3 px-3 text-right font-mono text-sm font-bold text-[#10b981] border-b border-[#10b981]/30" data-testid="in-hand-monthly">&#8377;{fmt(calc.inHand)}</td>
                <td className="py-3 px-3 text-right font-mono text-sm font-bold text-[#10b981] border-b border-[#10b981]/30" data-testid="in-hand-annual">&#8377;{fmt(ann(calc.inHand))}</td>
              </tr>
              <tr className="bg-[#10b981]/5">
                <td className="py-2 px-3 text-sm border-b-2 border-[#10b981]/30">In Hand + Extra Reimbursement</td>
                <td
                  className="py-2 px-3 text-right font-mono text-sm font-semibold text-[#10b981] border-b-2 border-[#10b981]/30"
                  data-testid="in-hand-after-extra-monthly"
                >
                  &#8377;{fmt(extraCalc.inHandSalaryAfterExtraReimbursement)}
                </td>
                <td
                  className="py-2 px-3 text-right font-mono text-sm font-semibold text-[#10b981] border-b-2 border-[#10b981]/30"
                  data-testid="in-hand-after-extra-annual"
                >
                  &#8377;{fmt(extraCalc.inHandSalaryAfterExtraReimbursementPerAnnum)}
                </td>
              </tr>

              {/* BONUS */}
              <SectionHeader label="Bonus" code="D" />
              <SalaryRow label="Bonus (8.33% of Basic) (Paid Annually)" monthly={bonus} annual={ann(bonus)} editable field="bonus" onChange={handleSalaryFieldChange} />

              {/* EMPLOYER CONTRIBUTIONS */}
              <SectionHeader label="Employer Contributions" code="E" />
              <SalaryRow label="Employer PF Contribution (13% of Basic)" monthly={employerPF} annual={ann(employerPF)} editable field="employerPF" onChange={handleSalaryFieldChange} />
              <SalaryRow label="ESIC (3.25% of Gross)" monthly={employerESIC} annual={ann(employerESIC)} editable field="employerESIC" onChange={handleSalaryFieldChange} />
              <SalaryRow label="Telephone Expense" monthly={telephoneExpense} annual={ann(telephoneExpense)} editable field="telephoneExpense" onChange={handleSalaryFieldChange} />
              <SalaryRow label="Mediclaim Insurance Premium" monthly={mediclaim} annual={ann(mediclaim)} editable field="mediclaim" onChange={handleSalaryFieldChange} />
              <SalaryRow label="Gratuity (4.81% of Basic)" monthly={gratuity} annual={ann(gratuity)} editable field="gratuity" onChange={handleSalaryFieldChange} />
              <SalaryRow label="Employer Contributions (E)" monthly={calc.employerTotal} annual={ann(calc.employerTotal)} highlight bold />

              {/* CTC */}
              <tr className="bg-[#0047AB]/10">
                <td className="py-3 px-3 text-sm font-bold border-b border-[#0047AB]/30">CTC (A + B + D + E)</td>
                <td className="py-3 px-3 text-right font-mono text-base font-bold text-[#0047AB] border-b border-[#0047AB]/30" data-testid="ctc-monthly">&#8377;{fmt(calc.ctc)}</td>
                <td className="py-3 px-3 text-right font-mono text-base font-bold text-[#0047AB] border-b border-[#0047AB]/30" data-testid="ctc-annual">&#8377;{fmt(ann(calc.ctc))}</td>
              </tr>
              <tr className="bg-[#0047AB]/5">
                <td className="py-2 px-3 text-sm border-t-0">CTC + Extra Reimbursement</td>
                <td
                  className="py-2 px-3 text-right font-mono text-sm font-bold text-[#0047AB]"
                  data-testid="ctc-after-extra-monthly"
                >
                  &#8377;{fmt(extraCalc.totalCtcAfterExtraReimbursement)}
                </td>
                <td
                  className="py-2 px-3 text-right font-mono text-sm font-bold text-[#0047AB]"
                  data-testid="ctc-after-extra-annual"
                >
                  &#8377;{fmt(extraCalc.totalCtcAfterExtraReimbursementPerAnnum)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
      <SalaryIncrementDialog
        open={incrementOpen}
        onOpenChange={setIncrementOpen}
        employeeId={employeeId}
        salary={sal}
        wageCaps={wageCaps}
        onApplied={onSalaryIncremented}
      />
    </Card>
  );
}
